import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { Modal, Input, Button, List, Popconfirm, Typography, Tooltip, Dropdown, Menu } from "antd";
import {
    DeleteOutlined,
    EditOutlined,
    SaveOutlined,
    CloseOutlined,
    DownloadOutlined,
} from "@ant-design/icons";
import file from "@app/lib/file";
import format from "@app/lib/format";

import "./style/manage-files-modal.scoped.scss";
import state from "@app/state/store/report/article";

const { Text } = Typography;

const ManageFilesModal = observer(({ article, visible, onCancel }) => {
    const [editingFileId, setEditingFileId] = useState(null);
    const [editingName, setEditingName] = useState("");

    const startEditing = (file) => {
        setEditingFileId(file._id);
        setEditingName(file.name.replace(".pdf", ""));
    };

    const cancelEditing = () => {
        setEditingFileId(null);
        setEditingName("");
    };

    const handleRename = async (file) => {
        if (editingName.trim()) {
            const newName = `${editingName.trim()}.pdf`;
            await state.renameSupplementaryFile(article._id, file._id, newName);
            cancelEditing();
        }
    };

    const handleDelete = async (fileId) => {
        await state.removeSupplementaryFile(article._id, fileId);
    };

    const allFiles = [
        {
            _id: article.file,
            name: `${article.title}`,
            isMain: true,
        },
        ...(article.supplementaryFiles || []).map((file) => ({
            ...file,
            isMain: false,
        })),
    ];

    const handleDownload = async (fileItem, index) => {
        const fileName = format.getFileName(article, fileItem, index);
        file.download(fileItem._id, fileName);
    };

    const handleDownloadWithHighlights = async (fileItem, index) => {
        const fileName = format.getFileName(article, fileItem, index);
        file.downloadWithHighlights(fileItem._id, fileName, article._id);
    };

    const renderFileActions = (file, index) => {
        const isEditing = editingFileId === file._id;

        return (
            <div className="file-actions">
                {!file.isMain && (
                    <>
                        {isEditing ? (
                            <>
                                <Button
                                    type="link"
                                    icon={<SaveOutlined />}
                                    onClick={() => handleRename(file)}
                                >
                                    Save
                                </Button>
                                <Button
                                    type="link"
                                    icon={<CloseOutlined />}
                                    onClick={cancelEditing}
                                >
                                    Cancel
                                </Button>
                            </>
                        ) : (
                            <Button
                                type="link"
                                icon={<EditOutlined />}
                                onClick={() => startEditing(file)}
                            >
                                Rename
                            </Button>
                        )}

                        <Popconfirm
                            title="Are you sure you want to delete this file?"
                            onConfirm={() => handleDelete(file._id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button type="link" danger icon={<DeleteOutlined />}>
                                Delete
                            </Button>
                        </Popconfirm>
                    </>
                )}

                <Dropdown
                    overlay={
                        <Menu>
                            <Menu.Item
                                key="download"
                                icon={<DownloadOutlined />}
                                onClick={() => handleDownload(file, index)}
                            >
                                Download
                            </Menu.Item>
                            <Menu.Item
                                key="download-highlights"
                                icon={<DownloadOutlined />}
                                onClick={() => handleDownloadWithHighlights(file, index)}
                            >
                                Download with highlights
                            </Menu.Item>
                        </Menu>
                    }
                    trigger={["click"]}
                >
                    <Button type="link" icon={<DownloadOutlined />}>
                        Download
                    </Button>
                </Dropdown>
            </div>
        );
    };

    const renderFileName = (file) => {
        const isEditing = editingFileId === file._id;

        if (isEditing && !file.isMain) {
            return (
                <Input
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onPressEnter={() => handleRename(file)}
                    suffix=".pdf"
                    autoFocus
                />
            );
        }

        return (
            <div className="file-name">
                <Tooltip title={file.name}>
                    <Text
                        className="file-name-text"
                        ellipsis={{
                            tooltip: file.name,
                            rows: 1,
                            expandable: true,
                        }}
                    >
                        {file.isMain && "(Article)"}
                        {file.name}
                    </Text>
                </Tooltip>
            </div>
        );
    };

    return (
        <Modal
            title="Manage Uploaded Files"
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width={800}
            className="manage-files-modal"
        >
            <List
                dataSource={allFiles}
                renderItem={(file, index) => (
                    <List.Item className="file-item">
                        {renderFileName(file)}
                        {renderFileActions(file, index)}
                    </List.Item>
                )}
            />
        </Modal>
    );
});

export default ManageFilesModal;
